import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from '@phosphor-icons/react';
import { ArrowRightIcon } from 'lucide-react';
interface CaseStudy {
  title: string;
  slug: string;
  description: string;
  image: string;
  tags: string[];
  category: string;
}
const caseStudies: CaseStudy[] = [{
  title: 'U.S. Bank Commercial Card',
  slug: 'us-bank',
  description: 'Lead UX Designer on enterprise B2B fintech platform. Achieved 98% user satisfaction and reduced processing time by 65% (54 days → 20 days) for corporate card applications.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cGTn1zWyBRLJq9z8XJ288f/usbthumb.png',
  tags: ['Digital Transformation', 'B2B', 'Growth'],
  category: 'FINANCIAL SERVICES'
}, {
  title: 'Cyber Security Redesign',
  slug: 'cyber-security',
  description: 'Product Designer for agency project in cyber security. Transformed complex threat analysis tools into intuitive B2B SaaS interface for enterprise clients.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/odCcwZqxVnrHSvVy5xeJrh/unisys_thumb.png',
  tags: ['Enterprise Software', 'B2B', 'SaaS'],
  category: 'DIGITAL PROTECTION'
}, {
  title: 'Faculty Information System Redesign',
  slug: 'case-western',
  description: 'Designed AI-powered faculty system achieving 85% accuracy and securing Amazon pilot program for 2026 rollout.',
  image: 'https://cdn.magicpatterns.com/uploads/r6QmYhNhTgdAoSYhnocL3u/CWRU_COMPV.png',
  tags: ['AI Innovation', '0-to-1', 'Human Systems'],
  category: 'AI EXPLORATION'
}, {
  title: 'Conversational AI Platform',
  slug: 'ai-comparison',
  description: 'Conversational AI Designer for agency project evaluating IBM Watson and Google Dialogflow. Designed natural dialogue flows reducing 100,000+ monthly calls through intelligent automation.',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/d2sojXsLucASkJnUpLu1Ks/Gemini_Generated_Image_qq6ptsqq6ptsqq6p.png',
  tags: ['AI Strategy', 'Conversational Design', '0-to-1'],
  category: 'AI COMPARISON'
}];
export function ParallaxCaseStudies() {
  const navigate = useNavigate();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const handleImageClick = (slug: string) => {
    // CRITICAL: Set flag that user is navigating from home page
    sessionStorage.setItem('navigatedFromHome', 'true');
    sessionStorage.removeItem('cameFromChat'); // Clear chat flag if it exists
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });
    navigate(`/case-study/${slug}`);
  };
  const handleMouseEnter = (index: number) => {
    setHoveredIndex(index);
    if (window.cursorSelectProject) {
      window.cursorSelectProject();
    }
  };
  const handleMouseLeave = () => {
    setHoveredIndex(null);
    if (window.cursorDefault) {
      window.cursorDefault();
    }
  };
  return <section className="relative w-full bg-black" id="projects" data-id="element-1813">
      {/* Sticky Header Container */}
      <div className="relative" data-id="element-1814">
        {/* Sticky Header */}
        <div className="sticky top-0 z-[100] bg-white w-full flex items-center justify-center h-[80px] shadow-[0_2px_4px_rgba(0,0,0,0.05)]" data-id="element-1815">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-black uppercase leading-tight" style={{
          fontFamily: "'Gefika', sans-serif",
          fontWeight: 400,
          letterSpacing: '0.02em'
        }} data-id="element-1816">
            Case Studies
          </h2>
        </div>

        {/* Case Study Stack Container */}
        <div className="relative w-full" data-id="element-1817">
          {caseStudies.map((study, index) => {
          return <div key={study.slug} className="relative w-full h-screen overflow-hidden flex items-center" onClick={() => handleImageClick(study.slug)} onMouseEnter={() => handleMouseEnter(index)} onMouseLeave={handleMouseLeave} style={{
            cursor: 'pointer'
          }} data-id="element-1818">
                {/* Background Image */}
                <div className="absolute inset-0 w-full h-full" data-id="element-1819">
                  <img src={study.image} alt={study.title} className="w-full h-full object-cover transition-transform duration-700 ease-out" style={{
                objectPosition: study.slug === 'us-bank' ? 'top' : 'center',
                transform: hoveredIndex === index ? 'scale(1.05)' : 'scale(1)'
              }} data-id="element-1820" />

                  {/* FIXED: Darker gradient overlay on mobile for better text readability */}
                  <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-transparent md:from-black/90 md:via-black/50" data-id="element-1821"></div>
                  <div className="absolute inset-0 bg-black/30 md:bg-black/20" data-id="element-1822"></div>
                </div>

                {/* Content Container - Left Aligned */}
                <div className="relative z-20 w-full max-w-[1400px] mx-auto px-6 sm:px-12 md:px-16 lg:px-24 h-full flex flex-col justify-center items-start pointer-events-none" data-id="element-1823">
                  <div className="max-w-xl lg:max-w-2xl" data-id="element-1824">
                    {/* Category */}
                    <p className="text-xs sm:text-sm md:text-sm lg:text-base font-medium uppercase mb-4 text-white md:text-white/60 tracking-[0.2em] reveal" style={{
                  fontFamily: "'Ronzino', sans-serif"
                }} data-id="element-1825">
                      {study.category}
                    </p>

                    {/* Title */}
                    <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white leading-[1.1] mb-6 text-left reveal" style={{
                  fontFamily: "'Gefika', sans-serif",
                  fontWeight: 400
                }} data-id="element-1826">
                      {study.slug === 'us-bank' ? <>
                          U.S. Bank
                          <br data-id="element-1827" />
                          Commercial Card
                        </> : study.slug === 'case-western' ? <>
                          Faculty Information
                          <br data-id="element-1828" />
                          System Redesign
                        </> : study.title}
                    </h3>

                    {/* Tags - FIXED: Better contrast on mobile */}
                    <div className="flex flex-wrap justify-start gap-2 mb-8 reveal lg:gap-3 lg:mb-10" data-id="element-1829">
                      {study.tags.map((tag, i) => <span key={i} className="px-3 py-1 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-[10px] sm:text-xs md:text-xs lg:text-sm text-white uppercase tracking-wider md:bg-white/5 md:border-white/10 lg:px-4 lg:py-1.5" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-1830">
                          {tag}
                        </span>)}
                    </div>

                    {/* Description - Match hero p text size and font exactly */}
                    <p className="text-white leading-relaxed max-w-lg md:max-w-lg lg:max-w-xl text-left mb-10 lg:mb-12 reveal" style={{
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                  letterSpacing: '0.01em'
                }} data-id="element-1831">
                      {study.description}
                    </p>

                    {/* Button - View Case Study */}
                    <button onClick={e => {
                  e.stopPropagation();
                  handleImageClick(study.slug);
                }} className="group flex items-center gap-3 text-white pointer-events-auto lg:mt-12" data-cursor-shrink-target="true" onMouseEnter={() => window.cursorDefault && window.cursorDefault()} onMouseLeave={() => hoveredIndex === index && window.cursorSelectProject && window.cursorSelectProject()} data-id="element-1832">
                      <span className="text-xs sm:text-sm uppercase tracking-[0.15em] border-b border-white/30 pb-1 group-hover:border-white transition-colors duration-300 lg:text-base" style={{
                    fontFamily: "'Ronzino', sans-serif"
                  }} data-id="element-1833">
                        view case study
                      </span>
                      <ArrowRight className="w-4 h-4 text-white/70 group-hover:text-white group-hover:translate-x-1 transition-all duration-300 lg:w-5 lg:h-5" data-id="element-1834" />
                    </button>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}